import React from 'react';
import GetOptionLabel from './get-option-label';
import messages from './messages';
import NoOptionMessage from './no-option-message';
import {useCustomerOrdersForm} from './use-customer-order-form';
import Icon from '@ailibs/feather-react-ts';
import Breadcrumb from 'components/Common/Breadcrumb';
import AppBarActions from 'components/VerticalLayout/app-bar-actions';
import AsyncItemPicker from 'components/async-item-picker';
import Button from 'components/button';
import DateInput from 'components/date-input';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {Container} from 'reactstrap';
import {Alert, Card, CardBody, Col, Row} from 'reactstrap';
import {Directory} from 'types/directory';

const CreateCustomerOrdersPage = () => {
  const {t} = useTranslation();
  const {
    params,
    validationErrors,
    isLoading,
    errorMessage,
    submit,
    setParams,
    syncParams,
  } = useCustomerOrdersForm();

  return (
    <div className="page-content">
      <Helmet>
        <title> {t(...messages.title())}</title>
      </Helmet>

      <Container fluid>
        <Breadcrumb
          showHeading={false}
          breadcrumbItems={[
            {title: t('common.dashboard'), path: '/dashboard'},
            {
              title: t(...messages.customerOrders()),
              path: '/dashboard/customer-orders',
            },
            {title: t(...messages.title()), path: '#'},
          ]}
        />
        <div>
          <Card>
            <CardBody>
              <Row className="mb-2">
                <Col md={12}>
                  <AsyncItemPicker
                    endPoint="data/directories"
                    defaultValue
                    className="my-2"
                    getOptionLabel={(directory: Directory) => (
                      <GetOptionLabel directory={directory} />
                    )}
                    label={t(...messages.client())}
                    optionLabel=""
                    errors={validationErrors.directory_id}
                    value={params.client}
                    onChange={client => syncParams(client)}
                    noOptionsMessage={(e: any) => (
                      <NoOptionMessage newClient={e.inputValue} />
                    )}
                  />
                </Col>
                <Col md={12}>
                  <AsyncItemPicker
                    endPoint="data/commercial-types?client_order"
                    className="my-2"
                    defaultValue
                    label={t(...messages.customerOrderType())}
                    optionLabel=""
                    errors={validationErrors.commercial_type_id}
                    value={params.type}
                    onChange={type => setParams({...params, type})}
                  />
                </Col>
                {params.type?.code === 'CCH' ? (
                  <Col md={12}>
                    <AsyncItemPicker
                      endPoint="data/project-fee-types"
                      className="my-2"
                      label={t('invoiceGeneralInformation.projectFeesType')}
                      getOptionLabel={(l: any) => l.label}
                      getOptionValue={(l: any) => l.value}
                      errors={validationErrors.project_fee_type}
                      value={params.projectFeesType}
                      onChange={projectFeesType =>
                        setParams({...params, projectFeesType})
                      }
                    />
                  </Col>
                ) : null}
                <Col md={12}>
                  <AsyncItemPicker
                    endPoint="data/document-categories"
                    defaultValue
                    className="my-2"
                    label={t(...messages.customerOrderCategory())}
                    optionLabel=""
                    errors={validationErrors.document_category_id}
                    value={params.category}
                    onChange={category => setParams({...params, category})}
                  />
                </Col>
                <Col md={12}>
                  <AsyncItemPicker
                    endPoint="data/document-origins"
                    defaultValue
                    className="my-2"
                    label={t(...messages.customerOrderOrigin())}
                    optionLabel=""
                    errors={validationErrors.origin_id}
                    value={params.origin}
                    onChange={origin => setParams({...params, origin})}
                  />
                </Col>
                <Col md={12}>
                  <AsyncItemPicker
                    endPoint="data/languages"
                    defaultValue
                    className="my-2 text-capitalize"
                    label={t(...messages.customerOrderLanguage())}
                    getOptionLabel={(l: any) => l.label?.toLocaleLowerCase()}
                    errors={validationErrors.language}
                    value={params.language}
                    getOptionValue={(l: any) => l.label}
                    onChange={language => {
                      setParams({...params, language});
                    }}
                  />
                </Col>
                <Col md={12} className="my-2">
                  <DateInput
                    label={t(...messages.customerOrderBillingDate())}
                    value={params.order_date}
                    onChange={order_date => setParams({...params, order_date})}
                    errors={validationErrors.order_date}
                  />
                </Col>
                <Col md={12} className="mb-1">
                  <DateInput
                    label={t(...messages.customerOrderDeadline())}
                    value={params.due_date}
                    onChange={due_date => setParams({...params, due_date})}
                    errors={validationErrors.due_date}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={8}>
                  <AppBarActions>
                    <Button
                      isLoading={isLoading}
                      onClick={submit}
                      color="important"
                    >
                      <Icon name="check" size={15} className="me-1" />
                      {t('common.saveChanges')}
                    </Button>
                  </AppBarActions>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default CreateCustomerOrdersPage;
